import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { fetchRenderPage } from "../redux/slices/pageBuilderSlice";
import DOMPurify from "dompurify";

const RenderPage = () => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const [finalHtml, setFinalHtml] = useState("");

  const { isLoading, error, pageData, dynamicContent } = useSelector(
    (state) => state.page
  );

  useEffect(() => {
    if (id) {
      dispatch(fetchRenderPage(id));
    }
  }, [dispatch, id]);

  useEffect(() => {
    if (pageData?.content && dynamicContent) {
      const merged = injectDynamicContent(pageData.content, dynamicContent);
      setFinalHtml(DOMPurify.sanitize(merged));
    }
  }, [pageData, dynamicContent]);

  useEffect(() => {
    // Remove static preview items AFTER DOM updates
    const observer = new MutationObserver(() => {
      document
        .querySelectorAll("[data-editor-only]")
        .forEach((el) => el.remove());
    });

    const target = document.getElementById("rendered-page");
    if(target) {
      observer.observe(target, { childList: true, subtree: true });
    }

    return () => observer.disconnect();
  }, [finalHtml]);

  const injectDynamicContent = (html, dynamicContent) => {
    let updatedHtml = html;

    Object.entries(dynamicContent).forEach(([type, content]) => {
      let contentHtml = "";

      //Dynamic Products
      if (type === "dynamic-products" && content.length > 0) {
        const itemsHtml = content
          .map((item, index) => {
            const hasDiscount = item.discount && item.discount < item.price;
            const discountPercent = hasDiscount
              ? Math.round(((item.price - item.discount) / item.price) * 100)
              : 0;
            const imageUrl = item.image?.startsWith("http")
              ? item.image
              : `${window.location.origin}/storage/${item.image}`;
      
            const reviewsHtml = (item.reviews || [])
              .map((review) => {
                const initial = (review.user || "G")[0].toUpperCase();
                return `
                  <div class="flex items-start space-x-3 mb-2">
                    <div class="h-8 w-8 rounded-full bg-gray-200 flex items-center justify-center text-xs font-bold text-gray-600">${initial}</div>
                    <div>
                      <p class="text-sm font-medium text-gray-800">${review.user || "Guest"}</p>
                      <p class="text-xs text-gray-500 mb-1">⭐ ${review.rating}/5</p>
                      <p class="text-xs text-gray-600">${review.comment}</p>
                    </div>
                  </div>
                `;
              })
              .join("");
      
            return `
            <div class="product-item border rounded-xl shadow p-4 text-center relative bg-white hover:shadow-md transition">
              ${
                hasDiscount
                  ? `<span class="absolute top-2 left-2 bg-green-500 text-white text-xs font-bold px-2 py-1 rounded">${discountPercent}% OFF</span>`
                  : ""
              }
              <a href="/shop/${item.id}">
                <img src="${imageUrl}" alt="${item.name}" class="mx-auto h-48 w-full object-cover mb-2 rounded cursor-pointer" />
              </a>
              <h3 class="text-base font-semibold mb-1">${item.name}</h3>
              <p class="text-sm text-gray-500 mb-2">${item.description || "Description"}</p>
              <div class="text-sm space-x-2 mb-2">
                ${
                  hasDiscount
                    ? `<span class="line-through text-gray-400">₹${item.price}</span>
                       <span class="text-green-600 font-bold">₹${item.discount}</span>`
                    : `<span class="text-green-600 font-bold">₹${item.price}</span>`
                }
              </div>
              <button class="mt-2 border border-green-600 text-green-600 font-medium px-4 py-1 rounded hover:bg-green-100 transition">
                Quick View
              </button>
              ${
                item.reviews?.length
                  ? `<button class="view-reviews mt-2 text-sm text-green-600 underline" data-index="${index}">
                      View Reviews (${item.reviews.length})
                    </button>`
                  : ""
              }
              <div class="reviews-html hidden" id="reviews-${index}">
                ${reviewsHtml}
              </div>
            </div>
          `;
          })
          .join("");
      
        // Full page HTML with modal
        contentHtml = `
          <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 class="text-2xl font-bold text-center mb-6">Our Products</h2>
            <div class="grid gap-6 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
              ${itemsHtml}
            </div>
          </div>
      
          <!-- Review Modal -->
          <div id="reviewModal" class="fixed inset-0 z-50 hidden bg-black bg-opacity-50 flex items-center justify-center">
            <div class="bg-white w-full max-w-md p-6 rounded shadow-lg relative">
              <button id="closeReviewModal" class="absolute top-2 right-2 text-gray-600 hover:text-black text-xl">&times;</button>
              <h3 class="text-lg font-semibold mb-4">Product Reviews</h3>
              <div id="reviewModalContent" class="max-h-96 overflow-y-auto space-y-2 text-left text-sm text-gray-700"></div>
            </div>
          </div>
        `;
      
        // Add modal event listeners after rendering
        setTimeout(() => {
          document.querySelectorAll(".view-reviews").forEach((btn) => {
            btn.addEventListener("click", (e) => {
              const index = e.target.dataset.index;
              const reviewBlock = document.querySelector(`#reviews-${index}`);
              if (reviewBlock) {
                document.getElementById("reviewModalContent").innerHTML = reviewBlock.innerHTML;
                document.getElementById("reviewModal").classList.remove("hidden");
              }
            });
          });
      
          document.getElementById("closeReviewModal").addEventListener("click", () => {
            document.getElementById("reviewModal").classList.add("hidden");
            document.getElementById("reviewModalContent").innerHTML = "";
          });
        }, 0);
      }
      
      // --- Dynamic Categories ---
      if (type === "dynamic-categories" && content.length > 0) {
        const listItems = content
          .map(
            (item) => `
          <tr class="border-b border-gray-200 hover:bg-gray-50 transition">
            <td class="py-2 text-left text-green-700 font-medium">${item.name}</td>
            <td class="py-2 text-right text-green-700">${item.product_count} Products</td>
          </tr>
        `
                )
                .join("");
      
              contentHtml = `
          <section class="px-4 py-6 bg-white border rounded shadow-sm max-w-sm mx-auto">
            <h3 class="text-xl font-semibold text-gray-900 border-b pb-2 mb-4">Categories</h3>
            <table class="w-full text-sm">
              <tbody>
                ${listItems}
              </tbody>
            </table>
          </section>
        `;
      }

      // --- Dynamic Blogs ---
      if (type === "dynamic-blogs" && content.length > 0) {
        contentHtml = `
          <div class="overflow-x-auto pb-4">
            <div class="flex flex-wrap justify-center gap-4 min-w-full">
              ${content
                .map(
                  (item) => `
                <div class="min-w-[300px] max-w-[300px] bg-white rounded-xl shadow-md hover:shadow-xl transition p-4 flex-shrink-0">
                  <img src="${
                    item.image || "https://via.placeholder.com/300x200"
                  }"
                       alt="${item.title}" 
                       class="rounded mb-3 w-full h-40 object-cover" />
                  <h3 class="text-lg font-semibold mb-1 line-clamp-2">${
                    item.title
                  }</h3>
                  <p class="text-sm text-gray-500 mb-2 line-clamp-3">${
                    item.description
                  }</p>
                  <p class="text-xs text-gray-400">${item.author} • ${
                    item.date
                  }</p>
                  <a href="/blog/${
                    item.slug
                  }" class="mt-2 inline-block text-green-600 hover:underline text-sm">Read More</a>
                </div>
              `
                )
                .join("")}
            </div>
          </div>
        `;
      }

      // --- Most Purchased Products ---
      if(type === "most-purchased-products" && content.length > 0) {
        console.log("Rendering most-purchased-products", content);

        const itemsHtml = content
          .map((item) => {
            const hasDiscount =
              item.discount_price && item.discount_price < item.price;
              const discountPercent = hasDiscount
              ? Math.round(
                  ((item.price - item.discount_price) / item.price) * 100
                )
              : 0;

            return `
            <a href="/shop/${
              item.id
            }" class="product-item w-full sm:w-[220px] md:w-[240px] border rounded-xl shadow p-4 text-center relative bg-white hover:shadow-lg transition duration-300 m-2 block">
              ${
                hasDiscount
                  ? `<span class="absolute top-2 left-2 bg-green-500 text-white text-xs font-bold px-2 py-1 rounded">${discountPercent}% OFF</span>`
                  : ""
              }
              <img src="${item.image}" alt="${
              item.name
            }" class="mx-auto h-48 w-full object-cover mb-3 rounded" />
              <h3 class="text-base font-semibold mb-1">${item.name}</h3>
              <p class="text-sm text-gray-600 mb-1">🛒 Purchased: <strong>${
                item.purchase_count
              }</strong></p>
              <div class="text-sm space-x-2">
                ${
                  hasDiscount
                    ? `<span class="line-through text-gray-400">₹${item.price}</span>
                     <span class="text-green-600 font-bold">₹${item.discount_price}</span>`
                    : `<span class="text-green-600 font-bold">₹${item.price}</span>`
                }
              </div>
            </a>
          `;
          })
          .join("");

        // Insert into the right container
        const target = document.querySelector(
          '[data-type="most-purchased-products"]'
        );

        if (target) {
          target.innerHTML = itemsHtml;
        } else {
          console.warn(
            "Target container for most-purchased-products not found."
          );
        }
      }

      updatedHtml = updatedHtml.replace(
        new RegExp(
          `<section[^>]*data-type="${type}"[^>]*>[\\s\\S]*?<\\/section>`,
          "g"
        ),
        contentHtml
      );
    });

    return updatedHtml;
  };

  if (isLoading) return <div className="text-center py-10">Loading...</div>;
  if (error)
    return <div className="text-red-500 text-center py-10">Error: {error}</div>;
  if (!pageData)
    return (
      <div className="text-gray-500 text-center py-10">No page data found.</div>
  );

  const sanitizedHtml = DOMPurify.sanitize(pageData.html);
  const updatedHtml = injectDynamicContent(sanitizedHtml, dynamicContent);

  return (
    <div className="page-renderer">
      {/* Inject page CSS */}
      <style dangerouslySetInnerHTML={{ __html: pageData.css || "" }} />
      {/* Inject updated HTML */}
      <div dangerouslySetInnerHTML={{ __html: updatedHtml }} />
    </div>
  );
};

export default RenderPage;
